import { getLocalStorage } from 'helpers/StorageHelper';
import React from 'react';
import 'assets/scss/logoLoader.scss';

import LoaderRingSvg from './SVGIcons/LoaderRingSvg';
import LoaderRoutesSvg from './SVGIcons/LoaderRoutesSvg';
import MapSvg from './SVGIcons/MapSvg';
import PinSvg from './SVGIcons/PinSvg';

const backgroundMap = {
  light: { bg: '#ffffff', textColor: '#475569' },
  dark: { bg: '#0b1220', textColor: '#94a3b8' },
  transparent: { bg: 'transparent', textColor: '#475569' },
};

const defaultMessages = [
  'Finding your FFL dealer…',
  'Verifying nationwide availability…',
  'Preparing your order…',
];

export interface LogoLoaderProps {
  /**
   * The background mode of the loader.
   * 'light' - Solid white background (default)
   * 'dark' - Dark navy background
   * 'transparent' - No background
   */
  background?: 'light' | 'dark' | 'transparent';
  /**
   * Control the visibility of the loader component.
   */
  loading?: boolean;
  /**
   * Custom array of text messages that cycle sequentially below the loader.
   * Default: ['Finding your FFL dealer…', 'Verifying nationwide availability…', 'Preparing your order…']
   */
  messages?: string[];
  /**
   * Additional CSS class to apply to the loader wrapper.
   */
  extraClass?: string;
  /**
   * Whether to display the loader as a fixed full-screen overlay with z-index 9999.
   */
  overlay?: boolean;
  /**
   * Whether to set the wrapper height to 100vh.
   */
  fullscreen?: boolean;
  /**
   * Whether to apply the background color only to the circular logo area,
   * keeping the wrapper background transparent.
   */
  logoBg?: boolean;
}

export function LogoLoader({
  background,
  loading = true,
  messages,
  extraClass = '',
  overlay = true,
  fullscreen = true,
  logoBg = true,
}: LogoLoaderProps) {
  const activeThemeKey =
    background && background !== 'transparent'
      ? background
      : (getLocalStorage('app-theme') as 'light' | 'dark') || 'dark';

  const theme = backgroundMap[activeThemeKey] || backgroundMap.dark;
  const wrapperBgColor = logoBg
    ? 'transparent'
    : backgroundMap[activeThemeKey]?.bg || 'transparent';
  const statusMessages = messages && messages.length > 0 ? messages : defaultMessages;

  const [currentMsgIndex, setCurrentMsgIndex] = React.useState(0);

  React.useEffect(() => {
    if (statusMessages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentMsgIndex((prev) => (prev + 1) % statusMessages.length);
    }, 1900); // 1.9s cycling matches the timing of 5.7s total for 3 messages
    return () => clearInterval(interval);
  }, [statusMessages]);

  if (!loading) return null;

  return (
    <div
      style={{ backgroundColor: wrapperBgColor }}
      className={`logo-loader-wrapper ${overlay ? 'logo-loader-overlay' : ''} ${
        fullscreen && !overlay ? 'logo-loader-fullscreen' : ''
      } ${extraClass}`}
    >
      <div className="logo-loader-content">
        <div
          style={logoBg ? { backgroundColor: theme.bg } : undefined}
          className={`logo-loader-visual ${logoBg ? `has-logo-bg has-logo-bg-${activeThemeKey}` : ''}`}
        >
          {/* Rotating progress ring */}
          <LoaderRingSvg className="logo-loader-ring" />

          {/* The logo mark: map + pin layered */}
          <div className="logo-loader-mark">
            {/* faint base map */}
            <MapSvg className="logo-loader-map-base" />

            {/* illuminated map, masked by a band that sweeps coast to coast */}
            <MapSvg className="logo-loader-map-sweep" />

            {/* route lines + nationwide points */}
            <LoaderRoutesSvg className="logo-loader-routes" />

            {/* pin glow + pin */}
            <div className="logo-loader-pin-glow-container">
              <div className="logo-loader-pin-glow" />
            </div>
            <PinSvg className="logo-loader-pin" />
          </div>
        </div>

        {/* cycling status message */}
        {statusMessages.length > 0 && (
          <div className="logo-loader-messages">
            <span
              key={currentMsgIndex}
              style={{ color: theme.textColor }}
              className="logo-loader-message"
            >
              {statusMessages[currentMsgIndex]}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default LogoLoader;
